import styled from "@emotion/styled";
import { Box, ThemeProvider, createTheme } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ko } from "date-fns/locale";
import { forwardRef, memo, useState } from "react";
import { Controller } from "react-hook-form";

// Constants
const CALENDAR_SCALE = 4;
const FONT_SIZE = "2rem";
const BUTTON_SIZE = {
  minWidth: "8rem",
  height: "4rem",
  padding: "8px 16px",
};

// Theme styles
const themeComponents = {
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          "&:hover fieldset, & fieldset, &.Mui-focused fieldset": {
            borderColor:
              "var(--semantic-secondary-stroke-normal-default, #D6D6D6)",
            borderWidth: "1px",
            boxShadow: "none",
          },
        },
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        fontSize: FONT_SIZE,
        "& .MuiPickersCalendarHeader-label, & .MuiPickersCalendarHeader-switchViewButton":
          {
            fontSize: FONT_SIZE,
          },
        "& .MuiPickersCalendarHeader-switchViewIcon": {
          fontSize: FONT_SIZE,
          width: "2rem",
          height: "2rem",
        },
      },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      header: {
        "& .MuiDayCalendar-weekDayLabel": {
          fontSize: FONT_SIZE,
        },
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      root: {
        "& .MuiPickersYear-yearButton": {
          fontSize: FONT_SIZE,
        },
      },
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      root: {
        "& .MuiPickersMonth-monthButton": {
          fontSize: FONT_SIZE,
        },
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        fontSize: FONT_SIZE,
        "& .MuiPickersDay-dayLabel": {
          fontSize: FONT_SIZE,
        },
        "&.Mui-selected": {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
          },
          "& .MuiPickersDay-dayLabel": {
            color: "inherit",
          },
        },
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      button: {
        fontSize: FONT_SIZE,
        "& .MuiSvgIcon-root": {
          fontSize: FONT_SIZE,
          width: "2rem",
          height: "2rem",
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        "& button": {
          fontSize: FONT_SIZE,
          ...BUTTON_SIZE,
        },
      },
    },
  },
};

const theme = createTheme({ components: themeComponents });

// Styled components
const datePickerStyles = {
  ".MuiPickersPopper-paper": {
    transform: `scale(${CALENDAR_SCALE})`,
    transformOrigin: "top left",
  },
  ".MuiPickersLayout-root .MuiPickersLayout-contentWrapper": {
    ".MuiDateCalendar-root": {
      ".MuiPickersCalendarHeader-root, .MuiDayCalendar-header, .MuiPickersDay-root":
        {
          fontSize: FONT_SIZE,
          "& > *": {
            fontSize: FONT_SIZE,
          },
        },
      ".MuiDayCalendar-weekDayLabel, .MuiPickersDay-root": {
        width: "3.5rem",
        height: "3.5rem",
      },
      ".MuiPickersArrowSwitcher-button": {
        fontSize: FONT_SIZE,
        ".MuiSvgIcon-root": {
          fontSize: FONT_SIZE,
          width: "2rem",
          height: "2rem",
        },
      },
    },
    ".MuiPickersLayout-actionBar button": {
      fontSize: FONT_SIZE,
      padding: "8px 16px",
    },
  },
};

const StyledDateTimePicker = styled(DateTimePicker)(datePickerStyles);

const DisplayBox = forwardRef(({ formikValues, displaySx }, ref) => (
  <Box
    pb={1}
    ref={ref}
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "0 px 10px 0",
      fontWeight: 500,
      fontSize: "0.95rem",
      letterSpacing: "2px",
      ...displaySx,
    }}
  >
    {formikValues || "날짜를 선택해주세요."}
  </Box>
));

const DateForm = forwardRef(
  (
    {
      label,
      isReadOnly,
      isDisabled,
      isTime,
      isDisplay,
      displaySx,
      onChange,
      value,
      name,
      control,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);

    if (isDisplay) {
      return <DisplayBox displaySx={displaySx} />;
    }

    return (
      <Controller
        name={name}
        control={control}
        defaultValue={value}
        render={({ field }) => (
          <ThemeProvider theme={theme}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ko}
            >
              <Box sx={{ width: "100%" }}>
                <StyledDateTimePicker
                  label={label}
                  value={field.value ? new Date(field.value) : null}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    if (onChange) {
                      onChange(newValue);
                    }
                  }}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  views={
                    isTime
                      ? ["year", "month", "day", "hours", "minutes"]
                      : ["year", "month", "day"]
                  }
                  minutesStep={15}
                  closeOnSelect={false}
                  open={open}
                  onClose={() => setOpen(false)}
                  onOpen={() => !isReadOnly && !isDisabled && setOpen(true)}
                  slots={{
                    toolbar: () => null,
                    tabs: () => null,
                  }}
                  slotProps={{
                    textField: {
                      onClick: () =>
                        !isReadOnly && !isDisabled && setOpen(true),
                      sx: {
                        width: "100%",
                        "& .MuiInputBase-root": {
                          fontSize: "14px",
                          color:
                            "var(--semantic-secondary-foreground-inverse-default, #999)",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 10px",
                          borderColor:
                            "var(--semantic-secondary-stroke-normal-default, #D6D6D6)",
                        },
                        "& .MuiButtonBase-root": {
                          padding: "0",
                          width: "28px",
                          height: "30px",
                          "& .MuiSvgIcon-root": {
                            fontSize: "2.5rem",
                            color:
                              "var(--semantic-secondary-foreground-strong-default, #333)",
                          },
                        },
                      },
                      ...props,
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </ThemeProvider>
        )}
      />
    );
  }
);

DateForm.displayName = "DateForm";

export default memo(DateForm);
